import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import { useSuggestionsShallowSelector } from 'components/WorkspaceEditor/SuggestionStore'
import { TrackedChange } from 'models/changes'
import { useCallback, useMemo } from 'react'
import { normalizeTrackChanges } from '../../../utils/TrackChangesAndSuggestionsUtils'
import { useTrackChangesUpdateShallowSelector } from '../store/TrackChangesUpdateStore'
import {
    TrackChangesAction,
    TrackChangesDataMap,
    TrackChangesMutationResult,
} from '../types'

const TRACK_CHANGE_ACTIONS = new Set([
    'accept',
    'discard',
    'acceptAll',
    'discardAll',
])

/**
 * Hook for optimistic UI updates after accepting/rejecting track changes
 * Provides type-safe mutation handling with error recovery and service access
 */
export const useTrackChangesUpdate = () => {
    // Access track changes service from store (merged from useTrackChangesService)
    const {
        updateTrackChanges,
        service,
    } = useTrackChangesUpdateShallowSelector((state) => ({
        updateTrackChanges: state.updateTrackChanges,
        service: state.service,
    }))
    const isServiceAvailable = !!service

    const { setIsTrackedChangesFetched } = useTrackChangesUpdateShallowSelector(
        (state) => ({
            setIsTrackedChangesFetched: state.setIsTrackedChangesFetched,
        })
    )
    const {
        trackChangesData,
        setTrackChangesData,
    } = useSuggestionsShallowSelector((state) => ({
        trackChangesData: state.trackChangesData,
        setTrackChangesData: state.setTrackChangesData,
    }))

    const { isProductTypeProject } = useElementItemShallowSelector((state) => ({
        isProductTypeProject: state.elementItemValue.isProductTypeProject,
    }))

    // Memoize the normalize function to prevent unnecessary re-creations
    const memoizedNormalizeTrackChanges = useMemo(
        () => (
            result: TrackedChange[],
            trackChangesData: TrackChangesDataMap
        ) => normalizeTrackChanges(result, trackChangesData),
        []
    )

    // Split action handlers for better performance and clarity
    const handleAcceptRejectTrackChangesAction = useCallback(
        async (result: TrackedChange[], action: TrackChangesAction) => {
            if (!result || result.length === 0) {
                throw new Error(`No data returned for ${action} action`)
            }

            const {
                trackChangesDataWithId,
                removeTrackChangesId,
            } = memoizedNormalizeTrackChanges(result, trackChangesData)

            if (Object.keys(trackChangesDataWithId).length === 0) {
                throw new Error(
                    'No track changes data found after mutation normalization'
                )
            }

            setTrackChangesData(trackChangesDataWithId, removeTrackChangesId)
            setIsTrackedChangesFetched?.(true)
        },
        [
            trackChangesData,
            setTrackChangesData,
            setIsTrackedChangesFetched,
            memoizedNormalizeTrackChanges,
        ]
    )

    const handleEmptyResult = useCallback(async () => {
        // For bulk actions, clear all data
        setTrackChangesData({})
        setIsTrackedChangesFetched?.(true)
    }, [setTrackChangesData, setIsTrackedChangesFetched])

    /**
     * Type-safe handler for track changes mutations with error handling
     * @param result - The mutation result data
     * @param action - The action to perform
     * @returns Promise with the original result or throws error
     */
    const handleUpdateAfterAcceptRejectTrackChanges = useCallback(
        async (
            result: TrackedChange[],
            action: TrackChangesAction
        ): Promise<TrackChangesMutationResult | undefined> => {
            if (!isServiceAvailable) {
                console.warn('Track changes service not available')
                return result as TrackChangesMutationResult
            }

            if (isProductTypeProject) {
                await updateTrackChanges()
                return result as TrackChangesMutationResult
            }

            try {
                if (TRACK_CHANGE_ACTIONS.has(action)) {
                    if (result?.length > 0) {
                        await handleAcceptRejectTrackChangesAction(
                            result,
                            action
                        )
                    } else {
                        await handleEmptyResult()
                    }
                }
                return result as TrackChangesMutationResult
            } catch (error) {
                const errorMessage = `Failed to handle ${action} operation: ${
                    error instanceof Error ? error.message : 'Unknown error'
                }`

                if (TRACK_CHANGE_ACTIONS.has(action)) {
                    console.warn(
                        'Track change mutation failed, attempting recovery:',
                        errorMessage
                    )
                    try {
                        await updateTrackChanges()
                    } catch (recoveryError) {
                        console.error('Recovery failed:', recoveryError)
                    }
                } else {
                    console.error(errorMessage)
                }
            }
        },
        [
            isServiceAvailable,
            isProductTypeProject,
            handleAcceptRejectTrackChangesAction,
            handleEmptyResult,
            updateTrackChanges,
        ]
    )

    return {
        handleUpdateAfterAcceptRejectTrackChanges,
        updateTrackChanges,
        isServiceAvailable,
    }
}
